import {
    Account,
    AccountGroupHierarchyNode,
} from "@common/types/domains/accounting/account";
import {
    groupAccountsByIdentity,
    MultiEntityAccount,
} from "@components/accounts/multiEntityAccountGrouping";
import {
    FormRuleActionType,
    TransactionRuleActionFormSchema,
} from "../constants";

type WorkspaceAccount = Account & { entityId: number };

const accountIdActionTypes = [
    FormRuleActionType.ACCOUNT_ID,
    FormRuleActionType.ACCRUAL_ACCOUNT_ID,
];

interface ClearInvalidAccountActionParams {
    actions: TransactionRuleActionFormSchema[];
    entityIds: number[] | undefined;
    accounts: WorkspaceAccount[] | undefined;
    accrualAccounts?: WorkspaceAccount[];
    groupsByEntityId: Map<number, AccountGroupHierarchyNode[]> | undefined;
    showAccountNumbers: boolean;
}

function storedAccountIds(
    value: TransactionRuleActionFormSchema["value"],
): string[] {
    if (value?.accountIds?.length) {
        return value.accountIds;
    }
    return value?.accountId ? [value.accountId] : [];
}

function haveSameIds(
    currentIds: string[] | undefined,
    nextIds: string[] | undefined,
): boolean {
    if (!currentIds || !nextIds) {
        return currentIds === nextIds;
    }
    return (
        currentIds.length === nextIds.length &&
        currentIds.every((id, index) => id === nextIds[index])
    );
}

function rederiveAccountActions({
    actions,
    rowsByActionType,
}: {
    actions: TransactionRuleActionFormSchema[];
    rowsByActionType: Map<FormRuleActionType, MultiEntityAccount[]>;
}) {
    let isChanged = false;

    const nextActions = actions.map((action) => {
        if (
            !action.actionType ||
            !accountIdActionTypes.includes(action.actionType)
        ) {
            return action;
        }

        const accountIds = storedAccountIds(action.value);

        if (accountIds.length === 0) {
            return action;
        }

        const rows = rowsByActionType.get(action.actionType);

        if (!rows) {
            return action;
        }

        const matchingRows = rows.filter((candidate) =>
            candidate.constituents.some((constituent) =>
                accountIds.includes(constituent.id),
            ),
        );

        const nextAccountIds =
            matchingRows.length === 1
                ? matchingRows[0].constituents.map(
                      (constituent) => constituent.id,
                  )
                : undefined;

        if (
            !action.value?.accountId &&
            !action.value?.accountName &&
            haveSameIds(action.value?.accountIds, nextAccountIds)
        ) {
            return action;
        }

        isChanged = true;

        return {
            ...action,
            value: {
                ...action.value,
                accountId: undefined,
                accountName: undefined,
                accountIds: nextAccountIds,
            },
        };
    });

    return isChanged ? nextActions : actions;
}

export function clearInvalidAccountActionForEntitySelection({
    actions,
    entityIds,
    accounts,
    accrualAccounts,
    groupsByEntityId,
    showAccountNumbers,
}: ClearInvalidAccountActionParams): TransactionRuleActionFormSchema[] {
    if (!entityIds?.length || !groupsByEntityId) {
        return actions;
    }

    const buildRows = (ledgerAccounts: WorkspaceAccount[]) =>
        groupAccountsByIdentity({
            accounts: ledgerAccounts,
            selectedEntityIds: entityIds,
            coverage: "all",
            identity: "nameTypePath",
            groupsByEntityId,
            showAccountNumbers,
        });

    const rowsByActionType = new Map<
        FormRuleActionType,
        MultiEntityAccount[]
    >();

    if (accounts) {
        rowsByActionType.set(
            FormRuleActionType.ACCOUNT_ID,
            buildRows(accounts),
        );
    }

    if (accrualAccounts) {
        rowsByActionType.set(
            FormRuleActionType.ACCRUAL_ACCOUNT_ID,
            buildRows(accrualAccounts),
        );
    }

    return rederiveAccountActions({ actions, rowsByActionType });
}
